import { ensureNodeId, type GraphNode } from "@needle-tools/addressing";
import { IJSONSchema } from "../../../../base/common/jsonSchema";
import { safeStringify } from "../../../../base/common/objects";
import { IObjectTargetService } from "../../../../editor/services/project/common/documentTarget";
import { IInstantiationService } from "../../../instantiation/common/instantiation";
import { ILogService } from "../../../log/common/log";
import { IResourceService, ResourceKind } from "../../../resources/common/resources";
import { IResourceAnalysisService } from "../../../webgl/three.js/resourceAnalysisService";
import { AIToolCallResponse, IAITool } from "../ai";
import { safeStringifyReplacer } from "./propertyTools";

/** Only surface duplication for a resource whose group wastes at least this much. */
const DUPLICATION_MIN_BYTES = 50 * 1024; // 50 KB

// #region List Resources Tool

type ListResourcesParams = {
    kind?: string;
    query?: string;
    pinnedOnly?: boolean;
    includeCollected?: boolean;
    includeMemory?: boolean;
    maxResults?: number;
    offset?: number;
};

/**
 * Lists the entries of the weak scene-resource registry (materials / textures /
 * geometries). Read-only: entries are dereferenced transiently for memory
 * estimation and never stored — the registry's no-retention constraint applies
 * to AI access exactly like to the UI.
 */
export class ListResourcesTool implements IAITool<ListResourcesParams> {
    readonly name = "resources_list";
    // Kept short on purpose: every tool description is re-sent on every round of
    // every turn (~7k tokens across all tools). Say what the tool is for and the
    // rules a model cannot guess; leave the rest to the response, which explains
    // itself. See plans/ai-tool-definition-size.md.
    readonly description =
        "List scene resources (materials, textures, geometries) with memory estimates and duplication analysis. " +
        "Each live entry carries a TARGET — pass it verbatim with a `path` to property_find / property_read / property.setValue; " +
        "that is the ONLY way to reach a resource, they are not hierarchy nodes. " +
        "Use for memory questions, finding materials/textures by name or type, and leak hunting. " +
        "The registry is weak: 'alive: false' means garbage collected. " +
        "Material ids can go to nodegraph_read for the shader graph.";
    readonly type = "function";
    readonly parametersJSONSchema: IJSONSchema = {
        type: "object",
        properties: {
            kind: {
                type: "string",
                description: "Filter by resource kind: 'material', 'texture' or 'geometry'. Omit for all kinds.",
            },
            query: {
                type: "string",
                description: "Optional case-insensitive regex matched against the resource name, type (e.g. 'MeshStandardMaterial') and uuid.",
            },
            pinnedOnly: {
                type: "boolean",
                description: "Only return entries the user pinned (the registry's only strong references). Default is false.",
                default: false,
            },
            includeCollected: {
                type: "boolean",
                description: "Also include entries whose resource was already garbage collected (name/type snapshots remain readable). Default is false — only live resources are returned.",
                default: false,
            },
            includeMemory: {
                type: "boolean",
                description: "Include estimated GPU/CPU memory (textures + geometries) and texture dimensions. Default is true.",
                default: true,
            },
            maxResults: {
                type: "number",
                description: "Maximum number of entries to return. Default is 50.",
                default: 50,
            },
            offset: {
                type: "number",
                description: "Number of matching entries to skip (pagination). Default is 0.",
                default: 0,
            },
        },
        required: [],
    };

    constructor(
        @IResourceService private readonly resources: IResourceService,
        @IResourceAnalysisService private readonly analysis: IResourceAnalysisService,
        @ILogService private readonly logger: ILogService,
        @IInstantiationService private readonly instantiation?: IInstantiationService,
    ) { }

    /** the ONE resolver, lazily — absent in products without addressing (chrome
     * until #38), where every element stays on the mint-on-first-request rung */
    private _targets: IObjectTargetService | null | undefined;
    private get targets(): IObjectTargetService | null {
        if (this._targets === undefined) {
            this._targets = this.instantiation?.invokeFunction(accessor =>
                (accessor.getIfExists(IObjectTargetService) as IObjectTargetService | undefined) ?? null) ?? null;
        }
        return this._targets;
    }

    async call(parameters?: ListResourcesParams): Promise<AIToolCallResponse> {
        if (!this.resources) {
            return { result: { message: "Resource registry not available.", success: false } };
        }

        const kind = parameters?.kind?.trim() || undefined;
        let regex: RegExp | null = null;
        if (parameters?.query) {
            try {
                regex = new RegExp(parameters.query, "i");
            }
            catch (e) {
                return { result: { message: `Invalid query regex: ${e}`, success: false } };
            }
        }
        const pinnedOnly = parameters?.pinnedOnly === true;
        const includeCollected = parameters?.includeCollected === true;
        const includeMemory = parameters?.includeMemory !== false;
        const maxResults = Math.max(1, parameters?.maxResults ?? 50);
        const offset = Math.max(0, parameters?.offset ?? 0);

        // per-kind counts always included — cheap and answers "how many" directly
        const counts: Record<string, unknown> = {};
        for (const [countKind, count] of this.resources.counts()) {
            counts[countKind] = { alive: count.alive, pinned: count.pinned, collected: count.collected };
        }

        const matched: ReturnType<ListResourcesTool["serializeEntry"]>[] = [];
        let totalMatched = 0;
        let listedMemoryBytes = 0;
        for (const entry of this.resources.entries(kind as ResourceKind | undefined)) {
            if (!includeCollected && !entry.alive) continue;
            if (pinnedOnly && !entry.pinned) continue;
            if (regex && !(regex.test(entry.name) || regex.test(entry.type) || (entry.uuid && regex.test(entry.uuid)))) continue;
            totalMatched++;
            if (totalMatched <= offset) continue;
            if (matched.length >= maxResults) continue; // keep counting totalMatched
            const serialized = this.serializeEntry(entry, includeMemory);
            if (typeof serialized.memory?.gpuBytes === "number" || typeof serialized.memory?.cpuBytes === "number") {
                listedMemoryBytes += Math.max(serialized.memory.gpuBytes ?? 0, serialized.memory.cpuBytes ?? 0);
            }
            matched.push(serialized);
        }

        // Scene-wide duplication summary (independent of pagination) — significant
        // byte-identical duplicate groups the user could dedupe to reclaim memory.
        let duplication: unknown;
        const report = includeMemory ? this.analysis.getReport() : null;
        if (report) {
            const groups: { kind: string; count: number; reclaimableBytes: number; entryIds: number[] }[] = [];
            for (const kind of Object.keys(report.byKind) as ResourceKind[]) {
                for (const g of report.byKind[kind]?.groups ?? []) {
                    const reclaim = Math.max(g.reclaimVram, g.reclaimCpu);
                    if (reclaim >= DUPLICATION_MIN_BYTES) groups.push({ kind, count: g.count, reclaimableBytes: reclaim, entryIds: g.entryIds });
                }
            }
            groups.sort((a, b) => b.reclaimableBytes - a.reclaimableBytes);
            duplication = {
                totalReclaimableGpuBytes: report.totalRedundantVram,
                totalReclaimableCpuBytes: report.totalRedundantCpu,
                significantGroups: groups.slice(0, 20),
                note: "Each group is byte-identical resources stored separately; reuse a single object to reclaim the bytes.",
            };
        }

        const responseData = {
            counts,
            totalMatched,
            totalReturned: matched.length,
            hasMore: totalMatched > offset + matched.length,
            pagination: { offset, limit: maxResults },
            /** max(gpu, cpu) summed over the RETURNED entries only */
            listedMemoryBytes: includeMemory ? listedMemoryBytes : undefined,
            /** significant byte-identical duplication across the whole scene */
            duplication,
            entries: matched,
        };

        return {
            result: {
                message: `${matched.length} of ${totalMatched} matching resources returned.`,
                aiMessage: safeStringify(responseData, undefined, safeStringifyReplacer),
                success: true,
            },
        };
    }

    private serializeEntry(entry: import("../../../resources/common/resources").IResourceEntry, includeMemory: boolean) {
        // the document TARGET (1h): the ONE resolver first — a FILE-BACKED
        // element gets its DETERMINISTIC derived id (+ the owning ref) there.
        // Load-bearing order: ids are never overwritten, so a random mint here
        // would beat the derived id, and the reopen re-derivation would orphan
        // every edit made against the listed target. Only the SCRATCH remainder
        // rides mint-on-first-request (the same generate-on-first-request rule
        // as user-born nodes; the save's capture bakes exactly this key). A
        // collected resource cannot be addressed — name/type snapshot only.
        const resource = entry.deref();
        const target = resource
            ? this.targets?.targetFor(resource) as { ref?: string; address?: { type?: string; id?: string; name?: string } } | null | undefined
                ?? { address: { type: entry.kind, id: ensureNodeId(resource as GraphNode), ...(entry.name ? { name: entry.name } : {}) } }
            : undefined;
        const data: {
            target?: { ref?: string; address?: { type?: string; id?: string; name?: string } };
            id: number;
            kind: string;
            type: string;
            name?: string;
            alive: boolean;
            pinned?: boolean;
            /** texture dimensions, e.g. "2048x2048" */
            size?: string;
            memory?: { gpuBytes?: number; cpuBytes?: number };
            /** present only when this resource is a significant byte-identical duplicate */
            duplication?: { copies: number; shared: boolean; reclaimableBytes: number };
        } = {
            target,
            id: entry.id,
            kind: entry.kind,
            type: entry.type,
            name: entry.name || undefined,
            alive: entry.alive,
            pinned: entry.pinned || undefined,
        };
        // memory + duplication come from IResourceAnalysisService (computed on idle,
        // deduped by allocation — shared storage counted once). No deref here.
        if (includeMemory && entry.alive) {
            const mem = this.analysis.getEntryMemory(entry.id);
            if (mem && (mem.vram > 0 || mem.cpu > 0)) {
                data.memory = { gpuBytes: mem.vram, cpuBytes: mem.cpu };
            }
            if (mem && typeof mem.width === "number" && typeof mem.height === "number") {
                data.size = `${mem.width}x${mem.height}`;
            }
            const dup = this.analysis.getEntryDuplication(entry.id);
            if (dup) {
                const reclaim = Math.max(dup.reclaimVram, dup.reclaimCpu);
                if (reclaim >= DUPLICATION_MIN_BYTES) {
                    data.duplication = { copies: dup.copies, shared: dup.shared, reclaimableBytes: reclaim };
                }
            }
        }
        return data;
    }
}
